import { useRef } from "react";
import { Upload } from "lucide-react";
import {
  BrowserRepository,
  MemoryRepository,
  type StoreState,
} from "@proof/core";
import { JsonImportButton } from "@proof/authoring/json-import";

const count = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function readBackup(value: unknown): StoreState {
  const state = value as Partial<StoreState>;
  if (
    !state ||
    typeof state !== "object" ||
    !Array.isArray(state.projects) ||
    !Array.isArray(state.resources)
  )
    throw new Error(
      "This isn't a workspace backup. Choose the file saved with “Download workspace backup”.",
    );
  const full: StoreState = {
    history: [],
    published: [],
    proofs: [],
    ...state,
  } as StoreState;
  // Throws with field-level detail when anything inside is invalid.
  new MemoryRepository([]).importState(full);
  return full;
}

/** Restore a browser workspace from a backup file, with one-step undo. */
export function WorkspaceRestore({
  repository,
  onRestored,
  notify,
}: {
  repository: BrowserRepository;
  onRestored: () => void | Promise<void>;
  notify: (message: string) => void;
}) {
  const previous = useRef<StoreState | undefined>(undefined);
  return (
    <JsonImportButton
      label="Restore workspace backup"
      icon={<Upload size={15} aria-hidden />}
      title="Restore a workspace backup"
      description={
        <p>
          Choose a file saved with “Download workspace backup”. Restoring
          replaces every project, library item and setting in this browser
          workspace with the backup. You can undo it right after.
        </p>
      }
      maxBytes={100_000_000}
      inspect={(value) => {
        const backup = readBackup(value);
        const current = repository.exportState();
        const tenants = new Set(backup.projects.map((p) => p.tenantId));
        return {
          title: "Workspace backup",
          summary: [
            {
              label: "Projects",
              value: `${backup.projects.length} (now ${current.projects.length})`,
            },
            {
              label: "Library items",
              value: `${backup.resources.length} (now ${current.resources.length})`,
            },
            { label: "Saved proofs", value: String(backup.proofs.length) },
            { label: "Revisions", value: String(backup.history.length) },
          ],
          warnings:
            tenants.size > 1
              ? [`The backup contains ${count(tenants.size, "workspace")}.`]
              : [],
          replaces: `Everything currently in this browser workspace (${count(current.projects.length, "project")}, ${count(current.resources.length, "library item")}) will be replaced.`,
          confirmLabel: "Restore backup",
        };
      }}
      onConfirm={async (value) => {
        const backup = readBackup(value);
        previous.current = repository.exportState();
        await repository.restoreState(backup);
        await onRestored();
        notify("Workspace restored from backup.");
        return `Workspace restored: ${count(backup.projects.length, "project")} and ${count(backup.resources.length, "library item")}.`;
      }}
      onUndo={async () => {
        if (!previous.current) return;
        await repository.restoreState(previous.current);
        previous.current = undefined;
        await onRestored();
        notify("Restore undone. Your previous workspace is back.");
      }}
    />
  );
}
